import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {
  AllowedControlPoint,
  AnalyzedGpsTrack,
  AnalyzedGpsTracksData,
  AnalyzeParameters,
  ControlPointsSettings,
  EventDay,
  EventResults,
  GpsPoint,
  Location,
  Punch,
  PunchStatus
} from 'src/app/domain/models';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ViewportScroller} from '@angular/common';
import {Title} from "@angular/platform-browser";
import {FractalService} from "../../services/fractal/fractal.service";
import {getSortedGroups, parseResults, sortResults, sortTeams2} from "../../domain/data-utls";
import {EventResultsTableComponent} from '../event-results-table/event-results-table.component';
import * as L from 'leaflet';
import {NotificationService} from "../../services/notifications/notification.service";

@Component({
  selector: 'app-analyze-gps-tracks',
  templateUrl: './analyze-gps-tracks.component.html',
  styleUrls: ['./analyze-gps-tracks.component.scss']
})
export class AnalyzeGpsTracksComponent implements OnInit, OnDestroy {

  results: EventResults;
  data: AnalyzedGpsTracksData = new AnalyzedGpsTracksData();
  sortedGroups: string[];
  currentEvent: EventDay;
  isLoading: boolean = true;
  isMapShowed: boolean = true;
  analyzeParameters: AnalyzeParameters;
  static map;
  searchTerm: string = '';
  selectedTrack: AnalyzedGpsTrack | null = null;
  mapLayers: L.Layer[] = [];
  isFiltered: boolean = false;
  private accuracyCircle: L.Circle | null = null;
  private selectedMarkerLocation: Location | null = null;
  timelineIndex: number = 0;
  maxTimelineIndex: number = 0;
  private playbackMarker: L.Marker | null = null;
  displayedPoints: GpsPoint[] = [];

  constructor(
    private actRoute: ActivatedRoute,
    private api: FractalService,
    private modalService: NgbModal,
    private scroller: ViewportScroller,
    private router: Router,
    private titleService: Title,
    private notifyService: NotificationService,
  ) {
    this.titleService.setTitle("Анализ треков");
  }

  ngOnInit(): void {
    this.analyzeParameters = new AnalyzeParameters();
    this.analyzeParameters.accuracy = 35;
    this.data = new AnalyzedGpsTracksData();
    this.data.tracks = [];
    this.reloadAll();
  }

  reloadAll() {
    this.isLoading = true;
    this.loadResults();
  }

  loadResults() {
    this.api.getResultsNew(this.actRoute.snapshot.params.id1, this.actRoute.snapshot.params.id2).subscribe((res) => {
      EventResultsTableComponent.cpSettings = res.controlPointsSettings;
      let newResult = parseResults(res);
      newResult = sortResults(newResult);
      this.sortedGroups = getSortedGroups(newResult);
      newResult = sortTeams2(newResult, "rogaine");
      this.results = newResult;
      this.notifyService.showSuccess("Результаты обновлены")
      const initLocation = this.results.controlPointsSettings[0]?.allowedControlPoints[0]?.location;
      if (initLocation) {
        this.initMap(initLocation).then(() => {
          this.addMarkersForControlPoints(this.results.controlPointsSettings[0]);
          this.loadGpsTracks();
        });
      } else {
        this.loadGpsTracks();
      }
    });

    this.api.GetDay(this.actRoute.snapshot.params.id, this.actRoute.snapshot.params.id2).subscribe((res) => {
      this.currentEvent = res;
    });
  }

  gpsPointsMap: Map<string, GpsPoint[]> = new Map();

  loadGpsTracks() {
    let ids = [];
    this.results.participants?.forEach(x => {
      x.teams.forEach(t => {
        t.runners.forEach(r => {
          if (r?.device?.id) {
            ids.push(r.device.id);
          }
        });
      });
    });

    if (ids.length === 0) {
      this.isLoading = false;
      return;
    }

    this.api.getGpsPoints(ids).subscribe((res) => {
      res.forEach(x => {
        if (!this.gpsPointsMap[x.deviceId]) {
          this.gpsPointsMap[x.deviceId] = [];
        }
        this.gpsPointsMap[x.deviceId].push(x);
      });

      this.results.participants?.forEach((x, xi) => {
        x.teams.forEach((t, ti) => {
          t.runners.forEach((r, ri) => {
            if (r?.device?.id) {
              const deviceId = r.device.id.toString();
              if (this.gpsPointsMap[deviceId]) {
                this.results.participants[xi].teams[ti].runners[ri].gpsPoints = this.gpsPointsMap[deviceId];
              }
            }
          });
        });
      });
      this.notifyService.showSuccess("GPS точки обновлены")

      this.isLoading = false;
      this.analyze();
    });
  }

  // Фильтрация GPS точек для исключения выбросов
  private filterGpsPoints(points: GpsPoint[]): GpsPoint[] {
    if (!points || points.length < 3) return points;

    const filteredPoints: GpsPoint[] = [points[0]]; // Всегда включаем первую точку
    const maxDistance = 0.1; // Максимальное расстояние между точками в км
    const maxSpeed = 30; // Максимальная скорость в км/ч
    const minTimeDiff = 5 * 60 * 1000; // Минимальный интервал времени между точками (5 минут)

    for (let i = 1; i < points.length; i++) {
      const prevPoint = points[i - 1];
      const currentPoint = points[i];

      // Проверяем время между точками
      const timeDiff = new Date(currentPoint.timestamp).getTime() - new Date(prevPoint.timestamp).getTime();

      // Если прошло слишком много времени, пропускаем эту точку
      if (timeDiff > minTimeDiff) {
        continue;
      }

      // Проверяем расстояние между точками
      const distance = this.calcCrow(
        prevPoint.latitude,
        prevPoint.longitude,
        currentPoint.latitude,
        currentPoint.longitude
      );

      // Проверяем скорость
      const speed = distance / (timeDiff / (1000 * 60 * 60));

      if (distance <= maxDistance && speed <= maxSpeed) {
        filteredPoints.push(currentPoint);
      }
    }

    return filteredPoints;
  }

  // Расчет угла между двумя точками
  private calculateBearing(startLat: number, startLng: number, endLat: number, endLng: number): number {
    const startLatRad = this.toRad(startLat);
    const endLatRad = this.toRad(endLat);
    const deltaLng = this.toRad(endLng - startLng);

    const y = Math.sin(deltaLng) * Math.cos(endLatRad);
    const x = Math.cos(startLatRad) * Math.sin(endLatRad) -
      Math.sin(startLatRad) * Math.cos(endLatRad) * Math.cos(deltaLng);

    let bearing = Math.atan2(y, x);
    bearing = bearing * (180 / Math.PI);
    bearing = (bearing + 360) % 360;

    return bearing;
  }

  // Добавление стрелочки направления движения
  private addDirectionArrow(startPoint: GpsPoint, endPoint: GpsPoint) {
    const startLatLng = L.latLng(startPoint.latitude, startPoint.longitude);
    const endLatLng = L.latLng(endPoint.latitude, endPoint.longitude);

    // Рассчитываем угол между точками
    const deltaLat = endPoint.latitude - startPoint.latitude;
    const deltaLng = endPoint.longitude - startPoint.longitude;
    const angle = Math.atan2(deltaLat, deltaLng) * (180 / Math.PI);

    // Создаем стрелочку с помощью полилинии
    const arrow = L.polyline([startLatLng, endLatLng], {
      color: '#000000',
      weight: 1,
      opacity: 0.8
    });

    // Добавляем стрелочку на карту
    arrow.addTo(AnalyzeGpsTracksComponent.map);
    this.mapLayers.push(arrow);

    // Добавляем маркер в виде стрелочки в конце линии
    const arrowIcon = L.divIcon({
      className: 'direction-arrow',
      html: `<i class="fas fa-arrow-right" style="color: #000000; font-size: 12px; transform: rotate(${angle}deg);"></i>`,
      iconSize: [12, 12]
    });

    const arrowMarker = L.marker(endLatLng, {
      icon: arrowIcon,
      rotationAngle: angle
    });

    arrowMarker.addTo(AnalyzeGpsTracksComponent.map);
    this.mapLayers.push(arrowMarker);
  }

  // Проверка на прямолинейность движения
  private isStraightMovement(points: GpsPoint[], startIndex: number, endIndex: number): boolean {
    if (endIndex - startIndex < 2) return false;

    const startPoint = points[startIndex];
    const endPoint = points[endIndex];
    const middlePoint = points[Math.floor((startIndex + endIndex) / 2)];

    // Рассчитываем отклонение средней точки от прямой линии
    const deviation = this.calculateDeviationFromLine(
      startPoint.latitude, startPoint.longitude,
      endPoint.latitude, endPoint.longitude,
      middlePoint.latitude, middlePoint.longitude
    );

    // Если отклонение меньше 10 метров, считаем движение прямолинейным
    return deviation < 0.01; // 0.01 км = 10 метров
  }

  // Расчет отклонения точки от прямой линии
  private calculateDeviationFromLine(
    startLat: number, startLng: number,
    endLat: number, endLng: number,
    pointLat: number, pointLng: number
  ): number {
    // Преобразуем координаты в метры
    const startX = startLng * 111320 * Math.cos(startLat * Math.PI / 180);
    const startY = startLat * 111320;
    const endX = endLng * 111320 * Math.cos(endLat * Math.PI / 180);
    const endY = endLat * 111320;
    const pointX = pointLng * 111320 * Math.cos(pointLat * Math.PI / 180);
    const pointY = pointLat * 111320;

    // Уравнение прямой: ax + by + c = 0
    const a = endY - startY;
    const b = startX - endX;
    const c = endX * startY - startX * endY;

    // Расстояние от точки до прямой
    const distance = Math.abs(a * pointX + b * pointY + c) / Math.sqrt(a * a + b * b);

    return distance / 1000; // Возвращаем в километрах
  }

  addTrackToMap(gpsPoints: GpsPoint[]) {
    if (!gpsPoints || gpsPoints.length === 0) return;
    if (!AnalyzeGpsTracksComponent.map) {
      console.warn('Карта не инициализирована, невозможно добавить трек');
      return;
    }

    let points = [];
    let colors = [];

    gpsPoints.sort((a, b) => {
      if (a.timestamp > b.timestamp) return 1;
      if (a.timestamp < b.timestamp) return -1;
      return 0;
    }).forEach(x => {
      let color = "";
      if (x.speed < 2) {
        color = "#fc2c03"; // Красный для медленного движения
      } else if (x.speed > 2 && x.speed < 3) {
        color = "#fcdb03"; // Желтый для среднего движения
      } else {
        color = "#39fc03"; // Зеленый для быстрого движения
      }
      colors.push(color);
      points.push({lat: x.latitude, lng: x.longitude, z: color});
    });

    const polylineOptions = {
      color: 'blue',
      weight: 6,
      opacity: 0.9,
      smoothFactor: 1,
    };

    const polyline = new L.Polyline(points, polylineOptions);
    this.setPolylineColors(polyline, colors);
    this.mapLayers.push(polyline);

    // Добавляем стрелочки только на прямых участках и реже
    const step = 20; // Проверяем каждые 20 точек
    for (let i = 0; i < points.length - step; i += step) {
      if (this.isStraightMovement(gpsPoints, i, i + step)) {
        // this.addDirectionArrow(gpsPoints[i], gpsPoints[i + step]);
      }
    }
  }

  // Converts numeric degrees to radians
  toRad(Value) {
    return Value * Math.PI / 180;
  }

  //This function takes in latitude and longitude of two location and returns the distance between them as the crow flies (in km)
  calcCrow(_lat1, _lon1, _lat2, _lon2) {
    var R = 6371; // km
    var dLat = this.toRad(_lat2 - _lat1);
    var dLon = this.toRad(_lon2 - _lon1);
    var lat1 = this.toRad(_lat1);
    var lat2 = this.toRad(_lat2);

    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c;
    return d;
  }

  markerOnClick(e) {
    alert("hi. you clicked the marker at " + e.latlng);
  }

  onMarkerClick(e: Location) {
    alert(e.longitude + "," + e.latitude);
  }

  analyze() {
    this.data.tracks = []
    var allowedControlPoints = this.results.controlPointsSettings[0].allowedControlPoints
    this.results?.participants?.forEach(p => {
      p.teams?.forEach(t => {
        t.runners?.forEach(r => {
          var punchesToCheck: AllowedControlPoint[] = [];
          allowedControlPoints.forEach(al => {
            if ((r?.device?.punchData?.punches?.find(e => e.minor === al.settings.minor) == null) &&
              r?.device?.punchData?.deletedPunches?.find(e => e.minor === al.settings.minor) == null) {
              punchesToCheck.push(al)
            }
          })

          var waitingApprove: Punch[] = []
          const filteredGpsPoints = this.filterGpsPoints(r?.gpsPoints || []);

          filteredGpsPoints.forEach(gpsPoint => {
            punchesToCheck.forEach(toCheck => {
              // Конвертируем метры в километры для расчета расстояния
              const calculatedDistance = this.calcCrow(
                toCheck.location.latitude,
                toCheck.location.longitude,
                gpsPoint.latitude,
                gpsPoint.longitude
              );

              // Проверяем, находится ли точка в пределах круга точности
              if (calculatedDistance <= (this.analyzeParameters.accuracy / 1000)) {
                var toAdd = new Punch();
                toAdd.uuid = toCheck.settings.uuid;
                toAdd.major = toCheck.settings.major;
                toAdd.minor = toCheck.settings.minor;
                toAdd.timestamp = new Date(gpsPoint.timestamp).toISOString();
                toAdd.location = new Location();
                toAdd.location.longitude = gpsPoint.longitude;
                toAdd.location.latitude = gpsPoint.latitude;
                toAdd.location.accuracy = gpsPoint.accuracy;
                toAdd.packetId = "gps-" + gpsPoint.id;
                toAdd.nearestDistance = calculatedDistance;
                toCheck.status = PunchStatus.waiting_approve;
                // Проверяем, есть ли уже такой панч в списке
                // if (toAdd.minor === 240 && +gpsPoint.timestamp > 1745148600000) {
                  var found = waitingApprove.find(x => x.major === toAdd.major && x.minor === toAdd.minor);
                  if (!found) {
                    waitingApprove.push(toAdd);
                  } else if (found.nearestDistance > toAdd.nearestDistance) {
                    // Если нашли более близкую точку, заменяем старую
                    const index = waitingApprove.indexOf(found);
                    waitingApprove[index] = toAdd;
                  }
                // }
              }
            });
          });

          r.newDevice.toCheck = punchesToCheck;
          var track = new AnalyzedGpsTrack();
          track.gpsTrack = filteredGpsPoints;
          track.participant = r;
          track.waitingApprovePunches = waitingApprove;
          this.data.tracks.push(track);
        });
      });
    });

    this.data.tracks = this.data.tracks.sort((a, b) => {
      if (a.waitingApprovePunches.length < b.waitingApprovePunches.length) {
        return 1;
      }
      if (a.waitingApprovePunches.length > b.waitingApprovePunches.length) {
        return -1;
      }
      return 0;
    });
  }

  private calculateCenterPoint(controlPoints: AllowedControlPoint[]): L.LatLng {
    if (!controlPoints || controlPoints.length === 0) {
      return new L.LatLng(0, 0);
    }

    let sumLat = 0;
    let sumLng = 0;
    let count = 0;

    controlPoints.forEach(point => {
      if (point.location) {
        sumLat += point.location.latitude;
        sumLng += point.location.longitude;
        count++;
      }
    });

    return new L.LatLng(sumLat / count, sumLng / count);
  }

  addMarkersForControlPoints(controlPoints: ControlPointsSettings) {
    if (!AnalyzeGpsTracksComponent.map) {
      console.warn('Карта не инициализирована, невозможно добавить маркеры');
      return;
    }

    if (this.accuracyCircle) {
      this.accuracyCircle.removeFrom(AnalyzeGpsTracksComponent.map);
      this.mapLayers = this.mapLayers.filter(layer => layer !== this.accuracyCircle);
      this.accuracyCircle = null;
    }

    controlPoints?.allowedControlPoints?.forEach(x => {
      if (!x?.location) {
        return;
      }

      var iconOptions = {
        iconUrl: 'src/assets/cp.png',
        iconSize: [40, 40]
      }

      var markerOptions = {
        title: x.name.replace("\n", ""),
        clickable: true,
        draggable: false
      }

      var marker = new L.Marker([x.location.latitude, x.location.longitude], markerOptions);
      marker.on('click', () => {
        this.showAccuracyCircle(x.location);
      });
      marker.addTo(AnalyzeGpsTracksComponent.map);
      this.mapLayers.push(marker);

      L.marker([x.location.latitude, x.location.longitude], {
        icon: L.divIcon({
          html: '<span style="color: black; justify-content: space-between;white-space: nowrap; font-weight: bold">' + x.name + '</span>',
          className: 'custom-marker',
        })
      })?.addTo(AnalyzeGpsTracksComponent.map);
    });
  }

  showAccuracyCircle(location: Location) {
    if (!AnalyzeGpsTracksComponent.map) {
      console.warn('Карта не инициализирована');
      return;
    }

    if (this.selectedMarkerLocation &&
      this.selectedMarkerLocation.latitude === location.latitude &&
      this.selectedMarkerLocation.longitude === location.longitude) {
      if (this.accuracyCircle) {
        this.accuracyCircle.removeFrom(AnalyzeGpsTracksComponent.map);
        this.mapLayers = this.mapLayers.filter(layer => layer !== this.accuracyCircle);
        this.accuracyCircle = null;
      }
      this.selectedMarkerLocation = null;
      return;
    }

    if (this.accuracyCircle) {
      this.accuracyCircle.removeFrom(AnalyzeGpsTracksComponent.map);
      this.mapLayers = this.mapLayers.filter(layer => layer !== this.accuracyCircle);
    }

    this.accuracyCircle = L.circle([location.latitude, location.longitude], {
      radius: this.analyzeParameters.accuracy,
      color: '#ff3300',
      fillColor: '#ff3300',
      fillOpacity: 0.1,
      weight: 1
    });

    this.accuracyCircle.addTo(AnalyzeGpsTracksComponent.map);
    this.mapLayers.push(this.accuracyCircle);
    this.selectedMarkerLocation = location;
  }

  clearMap() {
    if (!AnalyzeGpsTracksComponent.map) {
      return;
    }

    // Очищаем все слои с карты, кроме маркеров контрольных точек
    this.mapLayers.forEach(layer => {
      if (!(layer instanceof L.Marker)) {
        AnalyzeGpsTracksComponent.map.removeLayer(layer);
      }
    });
    this.mapLayers = this.mapLayers.filter(layer => layer instanceof L.Marker);
    if (this.playbackMarker) {
      AnalyzeGpsTracksComponent.map.removeLayer(this.playbackMarker);
      this.playbackMarker = null;
    }
    this.selectedTrack = null;
    this.selectedMarkerLocation = null;
    this.accuracyCircle = null;
  }

  showOnMap(data: AnalyzedGpsTrack) {
    if (!AnalyzeGpsTracksComponent.map) {
      console.warn('Карта не инициализирована');
      return;
    }

    if (this.selectedTrack === data) {
      this.clearMap();
      return;
    }

    this.clearMap();

    // Используем отфильтрованные или оригинальные точки в зависимости от состояния
    const pointsToShow = this.isFiltered ? this.filterGpsPoints(data.gpsTrack) : data.gpsTrack;
    this.addTrackToMap(pointsToShow);
    this.selectedTrack = data;
    this.displayedPoints = pointsToShow || [];
    this.maxTimelineIndex = Math.max(0, (this.displayedPoints?.length || 0) - 1);
    this.timelineIndex = 0;
    this.renderPlaybackMarker(this.timelineIndex);

    if (pointsToShow && pointsToShow.length > 0) {
      const bounds = L.latLngBounds(pointsToShow.map(point =>
        [point.latitude, point.longitude]
      ));
      AnalyzeGpsTracksComponent.map.fitBounds(bounds);
    }
  }

  analyzeControlPoints() {

  }

  analyzeGpsTracks() {

  }

  approve(punch: Punch, data: AnalyzedGpsTrack) {
    this.api.uploadPunch(data.participant?.device?.id, [punch]).subscribe(res => {
      res.success.forEach(x =>
        this.notifyService.showSuccess(x.message + ' - ' + x.status)
      );
      res.errors.forEach(x =>
        this.notifyService.showError(x.message + ' - ' + x.minor)
      )
    });
    // this.notifyService.showInfo(JSON.stringify(punch))
  }

  switchMap() {
    this.isMapShowed = !this.isMapShowed
    // if (this.isMapShowed) this.initMap();
  }

  ngAfterViewInit(): void {
    // Обновляем размер карты после инициализации компонента
    setTimeout(() => {
      if (AnalyzeGpsTracksComponent.map) {
        AnalyzeGpsTracksComponent.map.invalidateSize();
      }
    }, 300);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Обновляем размер карты при изменении размера окна
    if (AnalyzeGpsTracksComponent.map) {
      setTimeout(() => {
        AnalyzeGpsTracksComponent.map.invalidateSize();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Очистка при уничтожении компонента
  }

  private initMap(initCoords: Location): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Небольшая задержка для гарантии, что DOM элемент готов
        setTimeout(() => {
          try {
            if (!AnalyzeGpsTracksComponent.map) {
              var map = L.map('map').setView([initCoords.latitude, initCoords.longitude], 13);
              L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              }).addTo(map);
              AnalyzeGpsTracksComponent.map = map;

              // Принудительное обновление размера карты после инициализации
              setTimeout(() => {
                if (AnalyzeGpsTracksComponent.map) {
                  AnalyzeGpsTracksComponent.map.invalidateSize();
                }
                resolve();
              }, 100);
            } else {
              // Если карта уже существует, просто обновляем её размер
              AnalyzeGpsTracksComponent.map.invalidateSize();
              resolve();
            }
          } catch (error) {
            console.error('Ошибка инициализации карты:', error);
            reject(error);
          }
        }, 100);
      } catch (ex) {
        console.error('Ошибка инициализации карты:', ex);
        reject(ex);
      }
    });
  }

  setPolylineColors(line: L.Polyline, colors: string[]) {
    if (!AnalyzeGpsTracksComponent.map) {
      console.warn('Карта не инициализирована, невозможно добавить полилинию');
      return;
    }

    const latlngs = line.getLatLngs();

    latlngs.forEach((latlng, idx) => {
      if (idx + 1 < latlngs.length) {
        const poly = L.polyline([latlng, latlngs[idx + 1]], {color: colors[idx]});
        poly.addTo(AnalyzeGpsTracksComponent.map);
        this.mapLayers.push(poly);
      }
    });
  }

  addMarkers(markers: Location[], colors) {
    markers.forEach(x => {
      var marker = new L.Marker([x.latitude, x.longitude])
      marker.on('click', function () {
      })
      marker.addTo(AnalyzeGpsTracksComponent.map)
    })
  }

  // Добавляем фильтрацию треков
  get filteredTracks(): AnalyzedGpsTrack[] {
    if (!this.data?.tracks) {
      return [];
    }

    const filtered = this.data.tracks.filter(track => {
      if (!track?.participant) return false;

      const searchLower = this.searchTerm.toLowerCase();
      return (
        track.participant.name?.toLowerCase().includes(searchLower) ||
        track.participant.surname?.toLowerCase().includes(searchLower) ||
        track.participant.bib?.toString().includes(searchLower)
      );
    });

    return filtered;
  }

  // Конвертация расстояния в метры с округлением
  formatDistance(distance: number): string {
    return Math.round(distance * 1000) + ' м';
  }

  // Переключение фильтрации трека
  toggleFilter() {
    this.isFiltered = !this.isFiltered;
    if (this.selectedTrack) {
      const pointsToShow = this.isFiltered ? this.filterGpsPoints(this.selectedTrack.gpsTrack) : this.selectedTrack.gpsTrack;
      this.displayedPoints = pointsToShow || [];
      this.maxTimelineIndex = Math.max(0, (this.displayedPoints?.length || 0) - 1);
      this.timelineIndex = Math.min(this.timelineIndex, this.maxTimelineIndex);
      this.clearMap();
      this.addTrackToMap(this.displayedPoints);
      this.renderPlaybackMarker(this.timelineIndex);
    }
  }

  // Обновляем радиусы при изменении точности
  updateAccuracyCircles() {
    if (this.accuracyCircle) {
      this.accuracyCircle.setRadius(this.analyzeParameters.accuracy);
    }
  }

  // Обновляем HTML для обработки изменения точности
  onAccuracyChange() {
    this.updateAccuracyCircles();
  }

  onTimelineChange(index: number) {
    const idx = Math.max(0, Math.min(Number(index) || 0, this.maxTimelineIndex));
    this.timelineIndex = idx;
    this.renderPlaybackMarker(idx);
  }

  private renderPlaybackMarker(index: number) {
    if (!AnalyzeGpsTracksComponent.map) {
      return;
    }

    if (!this.displayedPoints || this.displayedPoints.length === 0) {
      if (this.playbackMarker) {
        AnalyzeGpsTracksComponent.map.removeLayer(this.playbackMarker);
        this.playbackMarker = null;
      }
      return;
    }

    const p = this.displayedPoints[index];
    const latLng = L.latLng(p.latitude, p.longitude);

    if (!this.playbackMarker) {
      const icon = L.divIcon({
        className: 'playback-marker',
        html: '<span class="dot"></span><span class="ring"></span>',
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });
      this.playbackMarker = L.marker(latLng, {
        draggable: false,
        icon
      });
      this.playbackMarker.addTo(AnalyzeGpsTracksComponent.map);
      this.mapLayers.push(this.playbackMarker);
    } else {
      this.playbackMarker.setLatLng(latLng);
    }
  }

}
